import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import Cookies from 'js-cookie';

import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { RootActions } from '@/store/types/action-types';
import { RootMutations } from '@/store/types/mutation-types';
import { SET_PREVIOUS_URL, TOGGLE_SPLASH_SCREEN } from '@/store/action-types';
import { RESET_ALL_FILTERS } from '@/store/modules/groupFilters/filters-types';
import { RESET_ALL as RESET_ALL_LISTS_DASHBOARDS } from '@/store/modules/listsDashboards/lists-dashboards-types';
import { SANITIZE_TABLE_OPTIONS } from '@/store/modules/tableOptions/types';
// BETA-CLEANUP: drop this import, the receiveSessionHandoff() call and `key: SIGN_IN_COOKIE`, and inline
// { secure: true, path: '/', sameSite: 'strict', expires: 5 } back into the cookie storage below
import {
  receiveSessionHandoff,
  SIGN_IN_COOKIE,
  SIGN_IN_COOKIE_EXPIRES_DAYS,
  signInCookieAttributes,
} from '@/bundles/App/helpers/betaChannel';
import { RESET_ACTIVE_MEMBERS } from '@/store/modules/members/members-types';

import instance from '../services/instance'
import router from '../router/router'
import WorkOrder from './modules/work-order'
import Members from './modules/members'
import Member from './modules/members/index.js'
import Availability from './modules/availability'
import Pipelines from './modules/pipelines'
import Company from './modules/company'
import Contact from './modules/contact'
import Occupier from './modules/occupier'
import Requirement from './modules/requirement'
import Property from './modules/property'
import Spatial from './modules/spatial'
import Settings from './modules/settings'
import SavedSets from './modules/savedSet'
import Admin from './modules/admin'
import Deals from './modules/deals'
import Views from './modules/views'
import ActivityFilters from './modules/activityFilters'
import Notifications from './modules/notifications'
import MeasurementUnits from './modules/measurementUnits';
import FiltersData from './modules/filtersData/index';
import GroupFilters from './modules/groupFilters';
import Subscriptions from './modules/subscriptions/index';
import TableOptions from './modules/tableOptions';
import Task from './modules/task';
import Lists from './modules/lists';
import Comparable from './modules/comparable';
import LeaseIngestion from './modules/leaseIngestion';
import AvailabilityIngestion from './modules/availabilityIngestion';
import ListsDashboards from './modules/listsDashboards';
import actions from './actions';
import mutations from './mutations';
import { getters } from './getters';
import { getInitialState } from './getInitialState';

// Every request registers and releases a cancel token (two mutations), and the
// persisted-state plugins re-serialise their whole slice on every mutation --
// 34 of the 41 writes measured over two navigations came from these three.
// Nothing persisted depends on them.
const TRANSIENT_MUTATIONS = new Set([
  RootMutations.ADD_CANCEL_TOKEN,
  RootMutations.REMOVE_CANCEL_TOKEN,
  RootMutations.CLEAR_CANCEL_TOKENS,
]);
const isPersistedMutation = (mutation) => !TRANSIENT_MUTATIONS.has(mutation.type);

// BETA-CLEANUP: before the persisted state below reads the sign-in
receiveSessionHandoff();

export default createStore({
  modules: {
    WorkOrder,
    Members,
    [StoreModulesNamesEnum.Member]: Member,
    Availability,
    Pipelines,
    Company,
    Contact,
    Occupier,
    Requirement,
    Property,
    Spatial,
    Settings,
    SavedSets,
    Admin,
    Comparable,
    Deals,
    Views,
    ActivityFilters,
    [StoreModulesNamesEnum.Notifications]: Notifications,
    [StoreModulesNamesEnum.MeasurementUnits]: MeasurementUnits,
    [StoreModulesNamesEnum.FiltersData]: FiltersData,
    [StoreModulesNamesEnum.GroupFilters]: GroupFilters,
    [StoreModulesNamesEnum.ListsDashboards]: ListsDashboards,
    Subscriptions,
    TableOptions,
    Task,
    Lists,
    [StoreModulesNamesEnum.LeaseIngestion]: LeaseIngestion,
    [StoreModulesNamesEnum.AvailabilityIngestion]: AvailabilityIngestion,
  },
  state: getInitialState(),
  plugins: [
    createPersistedState({
      paths: [
        'idToken',
        'userId',
        'contactId',
        'userLogo',
        'userName',
        'roles',
        'userEmail',
        'visitorId',
      ],
      key: SIGN_IN_COOKIE, // BETA-CLEANUP
      filter: isPersistedMutation,
      storage: {
        getItem: key => Cookies.get(key),
        setItem: (key, value) => Cookies.set(key, typeof value === 'object' ? JSON.stringify(value) : value, { ...signInCookieAttributes, expires: SIGN_IN_COOKIE_EXPIRES_DAYS }), // BETA-CLEANUP
        removeItem: key => Cookies.remove(key, signInCookieAttributes) // BETA-CLEANUP
      },
    }),
    createPersistedState({
      paths: [
        'color',
        'company',
        'companyIcon',
        'companyLogo',
        'availabilityView',
        'refreshToken',
        'userSettings',
        'appVersion',
        'isMultipleAccountAuth',
        'temporaryAuthToken',
        'temporaryUserInfo',
        'activeAccount',
        'GroupFilters',
        'ListsDashboards',
        'nav',
        'Views',
        'Subscriptions',
        'TableOptions',
        'Task',
        'Lists',
        'Settings',
        'otpRequireDialog'
      ],
      filter: isPersistedMutation,
      // Drops stale persisted page sizes.
      rehydrated: store => store.commit(`${StoreModulesNamesEnum.TableOptions}/${SANITIZE_TABLE_OPTIONS}`),
    }),
  ],
  mutations,
  actions: Object.assign(
    {
      loginAdmin ({ commit }, authData) {
        return new Promise((resolve, reject) => {
          instance
            .post('/oauth/jwttoken', authData)
            .then((res) => {
              if (res.data.access_token.includes('Wrong')) {
                reject(res.data.access_token)
              } else {
                commit(RootMutations.SET_ADMIN_TOKEN, res.data.access_token)
                resolve()
              }
            })
            .catch((error) => {
              reject(error)
            })
        })
      },
      // `rememberPage`: the session expired on a page (not an explicit sign-out), so sign-in
      // brings the user back to it instead of the dashboard.
      // The page is left first and the session cleared after: clearing first set `user` to null
      // while that page was still mounted, and a re-render reading it could crash the app. The
      // server sign-out may fail (session already expired, offline); the browser session is
      // cleared either way.
      async [RootActions.LOG_OUT] ({ commit, state }, { rememberPage = false } = {}) {
        // several 401s, or `?logout` seen twice, must not run a second sign-out alongside
        if (state.isLoggingOut) {
          return;
        }

        commit(RootMutations.SET_LOGGING_OUT, true);

        try {
          const currentRoute = router.currentRoute.value;
          if (rememberPage && currentRoute.matched.some(record => record.meta?.requiresToken)) {
            commit(SET_PREVIOUS_URL, currentRoute.fullPath);
          }

          // still signed in here: the welcome page's guard lets a signing-out user through
          await router.push({ name: 'welcome' }).catch(() => undefined);

          if (state.idToken) {
            try {
              await authService.logOut();
            } catch {
              // nothing to undo: the local session goes below whatever the server answered
            }
          }

          commit(RootMutations.CLEAR_AUTH_DATA);
          commit('GroupFilters/' + RESET_ALL_FILTERS, null, { root: true });
          commit(`${StoreModulesNamesEnum.ListsDashboards}/${RESET_ALL_LISTS_DASHBOARDS}`, null, { root: true });
          commit(`${StoreModulesNamesEnum.Member}/${RESET_ACTIVE_MEMBERS}`, null, { root: true });
          localStorage.clear();
          sessionStorage.clear();

          // a leave guard kept the page (unsaved changes): load the sign-in page instead of
          // leaving a signed-out user on a page that needs a session
          if (router.currentRoute.value.matched.some(record => record.meta?.requiresToken)) {
            window.location.assign(router.resolve({ name: 'welcome' }).href);
          }
        } finally {
          commit(RootMutations.SET_LOGGING_OUT, false);
          commit(TOGGLE_SPLASH_SCREEN, false);
        }
      },
      fetchUser ({ state }) {
        if (!state.idToken) {
          return
        }
        instance
          .get('/member/' + state.userId, {
            headers: {
              Authorization: 'Bearer ' + state.idToken,
            },
          })
          .catch((error) => {
            console.log(error)
          })
      },
      getBrokers (state) {
        instance
          .get('brokers', {
            headers: {
              Authorization: 'Bearer ' + state.idToken,
            },
          })
          .then(function (response) {
            return response.data.brokers
          })
          .catch(function (error) {
            console.log(error)
          })
      },
    },
    actions
  ),
  getters
});
